/**
 * Step4BuildServices
 */

import React, { useState, useEffect, useCallback, useMemo } from 'react';
import {
  BookmarkIcon,
  CheckCircleIcon,
  ArrowPathIcon,
  TrashIcon,
  PlusIcon,
  AdjustmentsHorizontalIcon,
  ClockIcon,
  ChevronDownIcon,
  ChevronUpIcon,
  PencilIcon,
  XMarkIcon,
  ExclamationTriangleIcon,
} from '@heroicons/react/24/outline';
import { formatCurrency } from '../../utils/bidWizardHelpers';
import { useRoleRateMap } from '../../../../hooks/useEmployeeRates';
import {
  useCreateBidScenario,
  useUpdateBidScenario,
} from '../../../../hooks/bid_wizard/useBidScenarios';
import {
  RANK_ALIASES,
  SERVICE_CATEGORIES,
  DEFAULT_MARGIN,
  MARGIN_MIN,
  MARGIN_MAX,
} from '../../../../components/bids/constants/bidWizardConstants';

const getRoleDisplayName = (role) => RANK_ALIASES[role] || role;

// ─── Utilities ────────────────────────────────────────────────────────────────

const calcBid = (services, rateMap, margin) => {
  let totalCost = 0, totalHours = 0, totalMinCost = 0, totalMaxCost = 0;
  for (const service of services) {
    for (const item of service.items) {
      const rate     = rateMap[item.role] ?? 0;
      const hours    = Number(item.hours)    || 0;
      const minHours = Number(item.minHours) || 0;
      const maxHours = Number(item.maxHours) || 0;
      totalCost    += rate * hours;
      totalHours   += hours;
      totalMinCost += rate * minHours;
      totalMaxCost += rate * maxHours;
    }
  }
  const divisor = 1 - margin / 100;
  const safe    = (n) => (divisor > 0 ? n / divisor : 0);
  return { totalCost, totalHours, bidValue: safe(totalCost), minBid: safe(totalMinCost), maxBid: safe(totalMaxCost) };
};

const buildScenarioPayload = ({ name, clientName, notes, wizardData, services, margin, rateOverrides, calculations, selectedTemplate, selectedBaseline }) => ({
  name:             name.trim(),
  clientName:       clientName.trim() || null,
  notes:            notes.trim()      || null,
  engagementType:   wizardData.engagementType,
  services,
  rateOverrides,
  margin,
  selectedTemplate: selectedTemplate ? { id: selectedTemplate.id || selectedTemplate.engagement_id, engagement_name: selectedTemplate.engagement_name } : null,
  selectedBaseline: selectedBaseline ? { id: selectedBaseline.id, label: selectedBaseline.label } : null,
  calculations: {
    bidValue:   calculations.bidValue,
    minBid:     calculations.minBid,
    maxBid:     calculations.maxBid,
    totalHours: calculations.totalHours,
    totalCost:  calculations.totalCost,
  },
});

const defaultScenarioName = (clientName) => {
  const now = new Date();
  return `${(clientName?.trim() || 'New Client')} — ${now.toLocaleDateString('en-US', { month: 'short' })} ${now.getFullYear()}`;
};

// ─── MarginControl ────────────────────────────────────────────────────────────

const MarginControl = ({ margin, onChange }) => {
  const [inputVal, setInputVal] = useState(String(margin));
  useEffect(() => setInputVal(String(margin)), [margin]);
  const clamp = (n) => Math.min(100, Math.max(0, Math.round(n)));
  const handleSlider = (e) => { const v = Number(e.target.value); setInputVal(String(v)); onChange(v); };
  const handleInput  = (e) => { setInputVal(e.target.value); const n = parseFloat(e.target.value); if (!isNaN(n)) onChange(clamp(n)); };
  const handleBlur   = ()  => { const n = parseFloat(inputVal); const v = isNaN(n) ? DEFAULT_MARGIN : clamp(n); onChange(v); setInputVal(String(v)); };
  const sliderPct  = ((Math.min(Math.max(margin, MARGIN_MIN), MARGIN_MAX) - MARGIN_MIN) / (MARGIN_MAX - MARGIN_MIN)) * 100;
  const trackColor = margin >= 30 ? '#009391' : margin >= 20 ? '#d1a800' : '#ed145b';
  return (
    <div className="space-y-2">
      <div className="flex items-center justify-between">
        <span className="text-xs font-semibold text-darkblue-500 flex items-center gap-1.5">
          <AdjustmentsHorizontalIcon className="w-3.5 h-3.5 text-grey-400" />
          Margin
        </span>
        <div className="flex items-center gap-1">
          <input type="number" value={inputVal} onChange={handleInput} onBlur={handleBlur} min={0} max={100}
            className="w-14 px-2 py-1 text-xs font-bold text-darkblue-500 border border-grey-300 rounded-md text-right outline-none focus:ring-2 focus:ring-brightblue-300" />
          <span className="text-xs font-semibold text-darkblue-500">%</span>
        </div>
      </div>
      <input type="range" min={MARGIN_MIN} max={MARGIN_MAX}
        value={Math.min(Math.max(margin, MARGIN_MIN), MARGIN_MAX)}
        onChange={handleSlider}
        className="w-full h-1.5 rounded-full appearance-none cursor-pointer"
        style={{ background: `linear-gradient(to right, ${trackColor} 0%, ${trackColor} ${sliderPct}%, #dcdeef ${sliderPct}%, #dcdeef 100%)` }}
      />
      <div className="flex justify-between text-xs text-grey-400">
        <span>{MARGIN_MIN}%</span><span>{MARGIN_MAX}%</span>
      </div>
    </div>
  );
};

// ─── BidSummary ───────────────────────────────────────────────────────────────

const BidSummary = ({ calculations, isRetainer, hasMissingRates }) => {
  const { bidValue, minBid, maxBid, totalHours, totalCost } = calculations;
  const rangeWidth = maxBid - minBid;
  const avgPct     = rangeWidth > 0 ? ((bidValue - minBid) / rangeWidth) * 100 : 50;
  return (
    <div className="bg-darkblue-500 rounded-xl p-4 text-white space-y-4">
      <div>
        <p className="text-xs font-semibold text-darkblue-200 uppercase tracking-widest mb-1">
          {isRetainer ? 'Monthly Bid' : 'Total Bid'}
        </p>
        <p className="text-3xl font-bold tracking-tight">{formatCurrency(bidValue)}</p>
        <p className="text-xs text-darkblue-300 mt-0.5">Cost basis: {formatCurrency(totalCost)}</p>
        {hasMissingRates && (
          <p className="text-xs text-amber-400 mt-1">&#9888; Cost basis incomplete — some roles missing rates</p>
        )}
      </div>
      {(minBid > 0 || maxBid > 0) && (
        <div className="space-y-2">
          <p className="text-xs font-semibold text-darkblue-200 uppercase tracking-widest">Range</p>
          <div className="relative h-2 bg-darkblue-400 rounded-full">
            <div className="absolute inset-0 bg-teal-400 rounded-full opacity-40" />
            <div className="absolute top-1/2 -translate-y-1/2 w-3 h-3 bg-white rounded-full shadow border-2 border-teal-400"
              style={{ left: `calc(${avgPct}% - 6px)` }} />
          </div>
          <div className="flex justify-between text-xs">
            <span className="text-teal-300">{formatCurrency(minBid)}</span>
            <span className="text-white font-semibold">{formatCurrency(bidValue)}</span>
            <span className="text-teal-300">{formatCurrency(maxBid)}</span>
          </div>
        </div>
      )}
      <div className="pt-2 border-t border-darkblue-400 flex items-center gap-1.5 text-xs text-darkblue-300">
        <ClockIcon className="w-3.5 h-3.5" />
        {totalHours.toFixed(1)} hrs{isRetainer ? '/mo' : ' total'}
      </div>
    </div>
  );
};

// ─── RatesSidebar ─────────────────────────────────────────────────────────────

const RatesSidebar = ({ roles, rateMap, ratesLoading, overrides, onOverrideChange, missingRateRoles = [] }) => (
  <div className="bg-grey-50 border border-grey-200 rounded-xl p-4 space-y-3">
    <p className="text-xs font-bold uppercase tracking-widest text-grey-400">Bill Rates</p>
    {ratesLoading ? (
      <div className="flex items-center gap-2 text-xs text-grey-400"><ArrowPathIcon className="w-3.5 h-3.5 animate-spin" />Loading...</div>
    ) : roles.length === 0 ? (
      <p className="text-xs text-grey-400">No rates configured</p>
    ) : (
      <div className="space-y-2">
        {roles.map((role) => {
          const base    = rateMap[role] ?? 0;
          const display = overrides[role] ?? base;
          return (
            <div key={role} className="flex items-center justify-between gap-2">
              <span className="text-xs text-darkblue-500 flex-1 truncate">{getRoleDisplayName(role)}</span>
              <div className="flex items-center gap-0.5 flex-shrink-0">
                <span className="text-xs text-grey-400">$</span>
                <input type="number" value={display} onChange={(e) => onOverrideChange(role, e.target.value)}
                  className="w-16 px-1.5 py-0.5 text-xs font-semibold text-darkblue-500 border border-grey-300 rounded text-right outline-none focus:ring-1 focus:ring-brightblue-300"
                  min={0} step={5} />
                <span className="text-xs text-grey-400">/hr</span>
              </div>
            </div>
          );
        })}
      </div>
    )}
    {missingRateRoles.length > 0 && (
      <div className="space-y-2 pt-3 mt-3 border-t border-amber-200">
        <p className="text-xs font-bold uppercase tracking-widest text-amber-600">Missing Rates</p>
        {missingRateRoles.map(({ role, totalHours }) => (
          <div key={role} className="flex items-center justify-between gap-2">
            <div className="flex-1 min-w-0">
              <span className="text-xs text-amber-700 truncate block">{getRoleDisplayName(role)}</span>
              <span className="text-xs text-amber-500">{totalHours.toFixed(1)} hrs</span>
            </div>
            <div className="flex items-center gap-0.5 flex-shrink-0">
              <span className="text-xs text-grey-400">$</span>
              <input type="number" value={overrides[role] ?? ''} onChange={(e) => onOverrideChange(role, e.target.value)}
                placeholder="0"
                className="w-16 px-1.5 py-0.5 text-xs font-semibold text-amber-700 border border-amber-300 rounded text-right outline-none focus:ring-1 focus:ring-amber-400 bg-amber-50"
                min={0} step={5} />
              <span className="text-xs text-grey-400">/hr</span>
            </div>
          </div>
        ))}
      </div>
    )}
  </div>
);

// ─── MarketContext ────────────────────────────────────────────────────────────

const MarketContext = ({ pricingContext }) => {
  if (!pricingContext) return null;
  return (
    <div className="bg-white border border-grey-200 rounded-xl p-4 space-y-2">
      <p className="text-xs font-bold uppercase tracking-widest text-grey-400">Market Context</p>
      {[
        { label: 'Min',    value: pricingContext.min },
        { label: 'Median', value: pricingContext.median, highlight: true },
        { label: 'Upper',  value: pricingContext.upper },
        { label: 'Max',    value: pricingContext.max },
      ].map(({ label, value, highlight }) => (
        <div key={label} className="flex justify-between text-xs">
          <span className="text-grey-500">{label}</span>
          <span className={highlight ? 'font-bold text-teal-600' : 'font-medium text-darkblue-500'}>
            ${(value || 0).toLocaleString()}
          </span>
        </div>
      ))}
    </div>
  );
};

// ─── ServiceCard (collapsible) ────────────────────────────────────────────────

const ServiceCard = React.memo(({
  service, serviceIdx, roles, effectiveRateMap, ratesLoading,
  onUpdateCategory, onUpdateRole, onUpdateHours,
  onAddItem, onRemoveItem, onRemoveService,
  defaultExpanded = true,
  missingRateSet,
}) => {
  const [expanded, setExpanded] = useState(defaultExpanded);

  const subtotalHours = service.items.reduce((s, it) => s + (Number(it.hours) || 0), 0);
  const subtotalCost  = service.items.reduce((s, it) => {
    const rate = effectiveRateMap[it.role] ?? 0;
    return s + rate * (Number(it.hours) || 0);
  }, 0);

  return (
    <div className="bg-white rounded-xl border border-grey-200 overflow-hidden">

      {/* ── Card header ── */}
      <div className="flex items-center gap-2 px-4 py-2.5 bg-grey-50 border-b border-grey-200">

        {/* Category dropdown */}
        <select
          value={service.category}
          onChange={(e) => onUpdateCategory(serviceIdx, e.target.value)}
          className="text-sm font-bold text-darkblue-500 bg-transparent border-none outline-none cursor-pointer focus:outline-none flex-shrink-0"
        >
          {SERVICE_CATEGORIES.map((cat) => (
            <option key={cat} value={cat}>{cat}</option>
          ))}
        </select>

        {/* Collapsed summary — role count + hours + cost */}
        {!expanded && service.items.length > 0 && (
          <div className="flex items-center gap-3 ml-1 flex-1 min-w-0">
            <span className="text-xs text-grey-400">
              {service.items.length} role{service.items.length !== 1 ? 's' : ''}
            </span>
            <span className="text-xs font-medium text-darkblue-500 tabular-nums">
              {subtotalHours.toFixed(1)}h
            </span>
            <span className="text-xs font-medium text-teal-600 tabular-nums">
              {formatCurrency(subtotalCost)}
            </span>
          </div>
        )}

        <div className="flex items-center gap-1 ml-auto flex-shrink-0">
          {/* Add role — only when expanded */}
          {expanded && (
            <button
              type="button"
              onClick={() => onAddItem(serviceIdx)}
              className="flex items-center gap-1 px-2 py-1 text-xs font-medium text-darkblue-500 hover:text-darkblue-700 hover:bg-grey-100 rounded transition-colors"
              title="Add role"
            >
              <PlusIcon className="w-3.5 h-3.5" />
              <span className="hidden sm:inline">Add role</span>
            </button>
          )}

          {/* Collapse toggle */}
          <button
            type="button"
            onClick={() => setExpanded((p) => !p)}
            className="p-1.5 text-grey-400 hover:text-darkblue-500 transition-colors rounded"
            title={expanded ? 'Collapse' : 'Expand'}
          >
            {expanded
              ? <ChevronUpIcon className="w-4 h-4" />
              : <ChevronDownIcon className="w-4 h-4" />
            }
          </button>

          {/* Delete */}
          <button
            type="button"
            onClick={() => onRemoveService(serviceIdx)}
            className="p-1.5 text-grey-300 hover:text-pink-500 transition-colors rounded"
            title="Remove service"
          >
            <TrashIcon className="w-3.5 h-3.5" />
          </button>
        </div>
      </div>

      {/* ── Expanded: role table ── */}
      {expanded && (
        <>
          {service.items.length > 0 && (
            <table className="w-full text-xs">
              <thead className="border-b border-grey-100">
                <tr className="bg-white">
                  <th className="px-4 py-2 text-left text-grey-400 font-semibold">Role</th>
                  <th className="px-4 py-2 text-center text-grey-400 font-semibold">Avg hrs</th>
                  <th className="px-4 py-2 text-center text-grey-400 font-semibold">Range</th>
                  <th className="px-4 py-2 text-right text-grey-400 font-semibold">Cost</th>
                  <th className="px-2 py-2" />
                </tr>
              </thead>
              <tbody className="divide-y divide-grey-50">
                {service.items.map((item, itemIdx) => {
                  const rate     = effectiveRateMap[item.role] ?? 0;
                  const lineCost = rate * (Number(item.hours) || 0);
                  const isMissingRate = missingRateSet?.has(item.role);
                  return (
                    <tr key={itemIdx} className={`transition-colors group ${isMissingRate ? 'bg-amber-50' : 'hover:bg-grey-50'}`}>
                      <td className="px-4 py-2">
                        <select value={item.role} onChange={(e) => onUpdateRole(serviceIdx, itemIdx, e.target.value)}
                          disabled={ratesLoading}
                          className="w-full text-xs text-darkblue-500 bg-transparent border-none outline-none cursor-pointer">
                          {item.role && !roles.includes(item.role) && (
                            <option value={item.role}>{getRoleDisplayName(item.role)}</option>
                          )}
                          {roles.map((role) => <option key={role} value={role}>{getRoleDisplayName(role)}</option>)}
                        </select>
                      </td>
                      <td className="px-4 py-2 text-center">
                        <input type="number" value={item.hours}
                          onChange={(e) => onUpdateHours(serviceIdx, itemIdx, 'hours', e.target.value)}
                          className="w-14 px-1.5 py-0.5 text-xs font-semibold text-darkblue-500 border border-grey-200 rounded text-right outline-none focus:ring-1 focus:ring-brightblue-300"
                          step="0.5" min="0" />
                      </td>
                      <td className="px-4 py-2 text-center">
                        <span className="inline-flex items-center gap-1 tabular-nums">
                          <input type="number" value={item.minHours ?? ''}
                            onChange={(e) => onUpdateHours(serviceIdx, itemIdx, 'minHours', e.target.value)}
                            className="w-12 px-1 py-0.5 text-xs text-grey-500 border border-grey-200 rounded text-right outline-none focus:ring-1 focus:ring-brightblue-200"
                            step="0.5" min="0" title="Min hours" />
                          <span className="text-grey-300">–</span>
                          <input type="number" value={item.maxHours ?? ''}
                            onChange={(e) => onUpdateHours(serviceIdx, itemIdx, 'maxHours', e.target.value)}
                            className="w-12 px-1 py-0.5 text-xs text-grey-500 border border-grey-200 rounded text-right outline-none focus:ring-1 focus:ring-brightblue-200"
                            step="0.5" min="0" title="Max hours" />
                        </span>
                      </td>
                      <td className="px-4 py-2 text-right font-medium tabular-nums">
                        {rate > 0
                          ? <span className="text-darkblue-500">{formatCurrency(lineCost)}</span>
                          : <span className="inline-flex items-center px-1.5 py-0.5 rounded text-xs font-semibold bg-amber-100 text-amber-700">No rate</span>
                        }
                      </td>
                      <td className="px-2 py-2">
                        <button type="button" onClick={() => onRemoveItem(serviceIdx, itemIdx)}
                          className="p-0.5 text-grey-200 hover:text-pink-500 transition-colors opacity-0 group-hover:opacity-100">
                          <TrashIcon className="w-3 h-3" />
                        </button>
                      </td>
                    </tr>
                  );
                })}
              </tbody>
              <tfoot className="border-t border-grey-200 bg-grey-50">
                <tr>
                  <td className="px-4 py-2 text-xs font-bold text-darkblue-500">Subtotal</td>
                  <td className="px-4 py-2 text-center text-xs font-bold text-darkblue-500 tabular-nums">
                    {subtotalHours.toFixed(1)}
                  </td>
                  <td className="px-4 py-2 text-center text-xs text-grey-400 tabular-nums">
                    {service.items.reduce((s, it) => s + (Number(it.minHours) || 0), 0).toFixed(1)}
                    <span className="mx-1 text-grey-300">–</span>
                    {service.items.reduce((s, it) => s + (Number(it.maxHours) || 0), 0).toFixed(1)}
                  </td>
                  <td className="px-4 py-2 text-right text-xs font-bold text-darkblue-500 tabular-nums">
                    {formatCurrency(subtotalCost)}
                  </td>
                  <td />
                </tr>
              </tfoot>
            </table>
          )}

          {service.items.length === 0 && (
            <div className="px-4 py-6 text-center">
              <p className="text-xs text-grey-400 mb-2">No roles added yet</p>
              <button type="button" onClick={() => onAddItem(serviceIdx)}
                className="inline-flex items-center gap-1.5 px-3 py-1.5 text-xs font-medium text-darkblue-500 border border-grey-300 rounded-lg hover:bg-grey-50 transition-colors">
                <PlusIcon className="w-3.5 h-3.5" />Add role
              </button>
            </div>
          )}
        </>
      )}
    </div>
  );
});

// ─── ScenarioDetailsPanel ─────────────────────────────────────────────────────

const ScenarioDetailsPanel = ({ scenarioName, setScenarioName, clientName, setClientName, notes, setNotes, isRetainer, engagementType, onSave, saveStatus, savedScenarioId }) => {
  const [open, setOpen] = useState(false);

  return (
    <div className="bg-white border border-grey-200 rounded-xl overflow-hidden">

      {/* Always-visible top bar */}
      <div className="flex items-center gap-3 px-4 py-2.5">
        {/* Scenario name — always visible, compact */}
        <input
          type="text"
          value={scenarioName}
          onChange={(e) => setScenarioName(e.target.value)}
          placeholder="Scenario name…"
          className="flex-1 min-w-0 px-2.5 py-1.5 border border-grey-300 rounded-lg text-xs text-darkblue-500 outline-none focus:ring-2 focus:ring-brightblue-300 focus:border-brightblue-400"
        />

        {/* Engagement type badge */}
        {engagementType && (
          <span className={`flex-shrink-0 inline-flex items-center px-2 py-1 rounded-md text-xs font-semibold ${
            isRetainer ? 'bg-violet-100 text-violet-700' : 'bg-blue-100 text-blue-700'
          }`}>
            {isRetainer ? 'Retainer' : 'Project'}
          </span>
        )}

        {/* Pencil — toggle details */}
        <button
          type="button"
          onClick={() => setOpen((p) => !p)}
          className={`p-1.5 rounded transition-colors flex-shrink-0 ${
            open ? 'text-darkblue-500 bg-grey-100' : 'text-grey-400 hover:text-darkblue-500'
          }`}
          title={open ? 'Hide details' : 'Edit client & notes'}
        >
          {open ? <XMarkIcon className="w-4 h-4" /> : <PencilIcon className="w-4 h-4" />}
        </button>

        {/* Save button */}
        <button
          type="button"
          onClick={onSave}
          disabled={saveStatus === 'saving' || !scenarioName.trim()}
          className={`flex items-center gap-1.5 px-3 py-1.5 text-xs font-semibold rounded-lg transition-colors whitespace-nowrap disabled:opacity-50 flex-shrink-0 ${
            saveStatus === 'saved' ? 'bg-teal-500 text-white' :
            saveStatus === 'error' ? 'bg-pink-500 text-white' :
            'bg-brand-500 text-white hover:bg-brand-600'
          }`}
        >
          {saveStatus === 'saving' ? <><ArrowPathIcon className="w-3.5 h-3.5 animate-spin" />Saving…</> :
           saveStatus === 'saved'  ? <><CheckCircleIcon className="w-3.5 h-3.5" />Saved</> :
           saveStatus === 'error'  ? 'Error — retry' :
           <><BookmarkIcon className="w-3.5 h-3.5" />{savedScenarioId ? 'Update' : 'Save'}</>}
        </button>
      </div>

      {/* Expandable: client name + notes */}
      {open && (
        <div className="border-t border-grey-100 px-4 py-3 space-y-2.5">
          <div className="flex items-center gap-3">
            <span className="text-xs font-semibold text-darkblue-500 whitespace-nowrap w-14">Client</span>
            <input
              type="text"
              value={clientName}
              onChange={(e) => setClientName(e.target.value)}
              placeholder="Client name (optional)…"
              className="flex-1 px-2.5 py-1.5 border border-grey-300 rounded-lg text-xs text-darkblue-500 outline-none focus:ring-2 focus:ring-brightblue-300"
            />
          </div>
          <div className="flex items-start gap-3">
            <span className="text-xs font-semibold text-darkblue-500 whitespace-nowrap w-14 pt-1.5">Notes</span>
            <textarea
              value={notes}
              onChange={(e) => setNotes(e.target.value)}
              placeholder="Scope assumptions, client context, caveats…"
              rows={2}
              className="flex-1 px-2.5 py-1.5 border border-grey-300 rounded-lg text-xs text-darkblue-500 outline-none focus:ring-2 focus:ring-brightblue-300 resize-none placeholder-grey-300"
            />
          </div>
        </div>
      )}
    </div>
  );
};

// ─── Main export ──────────────────────────────────────────────────────────────

const Step4BuildServices = ({
  wizardData,
  setWizardData,
  insights,
  pricingContext,
  componentsLoading,
  hasComponentsData,
  selectedTemplate,
  selectedBaseline,
  autoSyncing,
  autoSyncError,
  initialScenarioId,
  initialScenarioName,
  initialMargin,
  initialRateOverrides,
  initialClientName,
  initialNotes,
}) => {
  const { rateMap, roles, isLoading: ratesLoading } = useRoleRateMap({ enabled: true });

  const isLoaded = !!initialScenarioId; // true when loaded from saved scenario

  const [margin,          setMargin]          = useState(initialMargin        ?? DEFAULT_MARGIN);
  const [rateOverrides,   setRateOverrides]   = useState(initialRateOverrides ?? {});
  const [scenarioName,    setScenarioName]    = useState(initialScenarioName  ?? defaultScenarioName(wizardData.clientName));
  const [clientName,      setClientName]      = useState(initialClientName    ?? wizardData.clientName ?? '');
  const [notes,           setNotes]           = useState(initialNotes         ?? '');
  const [savedScenarioId, setSavedScenarioId] = useState(initialScenarioId   ?? null);
  const [saveStatus,      setSaveStatus]      = useState(null);

  useEffect(() => { if (initialMargin        !== undefined) setMargin(initialMargin); },                [initialMargin]);
  useEffect(() => { if (initialRateOverrides !== undefined) setRateOverrides(initialRateOverrides); },  [initialRateOverrides]);
  useEffect(() => { if (initialClientName    !== undefined) setClientName(initialClientName); },        [initialClientName]);
  useEffect(() => { if (initialNotes         !== undefined) setNotes(initialNotes ?? ''); },            [initialNotes]);
  useEffect(() => { if (initialScenarioId    !== undefined) setSavedScenarioId(initialScenarioId); },   [initialScenarioId]);
  useEffect(() => { if (initialScenarioName  !== undefined) setScenarioName(initialScenarioName); },    [initialScenarioName]);
  useEffect(() => {
    if (!savedScenarioId) setScenarioName(defaultScenarioName(wizardData.clientName));
  }, [wizardData.clientName]);

  const services   = wizardData.services || [];
  const isRetainer = wizardData.engagementType?.toLowerCase() === 'retainer';

  const effectiveRateMap = useMemo(() => ({
    ...rateMap,
    ...Object.fromEntries(
      Object.entries(rateOverrides)
        .filter(([, v]) => v !== '' && !isNaN(Number(v)))
        .map(([k, v]) => [k, Number(v)])
    ),
  }), [rateMap, rateOverrides]);

  const calculations = useMemo(
    () => calcBid(services, effectiveRateMap, margin),
    [services, effectiveRateMap, margin]
  );

  // Publish the marked-up bid value to wizardData so downstream steps
  // (e.g. Step 5 proposal generation) read the same number the user sees
  // on this page. For retainers the value is the monthly bid; for
  // projects it's the total bid.
  useEffect(() => {
    setWizardData((prev) => {
      const prevBid = prev.bidCalculations;
      if (
        prevBid &&
        prevBid.bidValue === calculations.bidValue &&
        prevBid.margin   === margin
      ) {
        return prev;
      }
      return {
        ...prev,
        bidCalculations: {
          bidValue: calculations.bidValue,
          margin,
        },
      };
    });
  }, [calculations.bidValue, margin, setWizardData]);

  const missingRateRoles = useMemo(() => {
    const acc = {};
    for (const service of services) {
      for (const item of service.items) {
        if (!effectiveRateMap[item.role]) {
          const hours = Number(item.hours) || 0;
          if (!acc[item.role]) acc[item.role] = { role: item.role, totalHours: 0 };
          acc[item.role].totalHours += hours;
        }
      }
    }
    return Object.values(acc);
  }, [services, effectiveRateMap]);

  const missingRateSet = useMemo(
    () => new Set(missingRateRoles.map((r) => r.role)),
    [missingRateRoles]
  );

  // ── Service mutation helpers ──────────────────────────────────────────────

  const setServices = useCallback((updater) => {
    setWizardData((p) => ({
      ...p,
      services: typeof updater === 'function' ? updater(p.services || []) : updater,
    }));
  }, [setWizardData]);

  const updateCategory  = useCallback((si, value) => setServices((prev) => prev.map((s, i) => i !== si ? s : { ...s, category: value })), [setServices]);
  const updateItemRole  = useCallback((si, ii, value) => setServices((prev) => prev.map((s, sIdx) => sIdx !== si ? s : { ...s, items: s.items.map((it, iIdx) => iIdx !== ii ? it : { ...it, role: value }) })), [setServices]);

  const updateItemHours = useCallback((si, ii, field, rawValue) => {
    setServices((prev) => prev.map((s, sIdx) => {
      if (sIdx !== si) return s;
      return {
        ...s,
        items: s.items.map((it, iIdx) => {
          if (iIdx !== ii) return it;
          if (field === 'hours') {
            const avg = parseFloat(rawValue) || 0;
            const useAuto = it.autoRange !== false;
            return { ...it, hours: rawValue, minHours: useAuto ? parseFloat((avg * 0.95).toFixed(1)) : it.minHours, maxHours: useAuto ? parseFloat((avg * 1.05).toFixed(1)) : it.maxHours };
          }
          return { ...it, [field]: rawValue, autoRange: false };
        }),
      };
    }));
  }, [setServices]);

  const addLineItem  = useCallback((si) => {
    const firstRole = roles[0] ?? '';
    setServices((prev) => prev.map((s, sIdx) => sIdx !== si ? s : { ...s, items: [...s.items, { role: firstRole, hours: 0, minHours: 0, maxHours: 0, autoRange: true, notes: '' }] }));
  }, [roles, setServices]);

  const removeLineItem = useCallback((si, ii) => setServices((prev) => prev.map((s, sIdx) => sIdx !== si ? s : { ...s, items: s.items.filter((_, iIdx) => iIdx !== ii) })), [setServices]);
  const removeService  = useCallback((si) => setServices((prev) => prev.filter((_, sIdx) => sIdx !== si)), [setServices]);

  const addService = useCallback(() => {
    const used = new Set((services || []).map((s) => s.category));
    const next  = SERVICE_CATEGORIES.find((c) => !used.has(c)) || 'General';
    setServices((prev) => [...prev, { category: next, items: [] }]);
  }, [services, setServices]);

  const handleRateOverride = useCallback((role, value) => {
    setRateOverrides((p) => ({ ...p, [role]: value }));
  }, []);

  // ── Save ──────────────────────────────────────────────────────────────────

  const createScenario = useCreateBidScenario();
  const updateScenario = useUpdateBidScenario();

  const handleSaveScenario = async () => {
    if (!scenarioName.trim()) return;
    setSaveStatus('saving');
    const payload = buildScenarioPayload({ name: scenarioName, clientName, notes, wizardData, services, margin, rateOverrides, calculations, selectedTemplate, selectedBaseline });
    try {
      if (savedScenarioId) {
        await updateScenario.mutateAsync({ scenarioId: savedScenarioId, ...payload });
      } else {
        const result = await createScenario.mutateAsync(payload);
        if (result?.id) setSavedScenarioId(result.id);
      }
      setSaveStatus('saved');
    } catch {
      setSaveStatus('error');
    } finally {
      setTimeout(() => setSaveStatus(null), 3000);
    }
  };

  // ── Render ────────────────────────────────────────────────────────────────

  return (
    <div className="space-y-4">

      {/* Actuals status badges */}
      {componentsLoading && !autoSyncing && (
        <div className="flex items-center gap-3 p-4 bg-blue-50 border border-blue-200 rounded-xl text-sm text-blue-700">
          <ArrowPathIcon className="w-4 h-4 animate-spin flex-shrink-0" />
          Loading actuals from reference engagement…
        </div>
      )}

      {autoSyncing && (
        <div className="flex items-center gap-3 p-4 bg-indigo-50 border border-indigo-200 rounded-xl text-sm text-indigo-700">
          <ArrowPathIcon className="w-4 h-4 animate-spin flex-shrink-0" />
          <div>
            <p className="font-medium">Syncing actuals from QuickBooks…</p>
            <p className="text-xs text-indigo-500 mt-0.5">This may take 10–30 seconds depending on data volume.</p>
          </div>
        </div>
      )}

      {autoSyncError && (
        <div className="p-4 bg-red-50 border border-red-200 rounded-xl">
          <p className="text-sm font-semibold text-red-800 mb-1">QuickBooks sync failed</p>
          <p className="text-xs text-red-600">{autoSyncError} Go back to Step 3 to retry or select a different reference.</p>
        </div>
      )}

      {!componentsLoading && !autoSyncing && !autoSyncError && services.length === 0 && selectedTemplate && selectedTemplate.qb_customer_ref && (
        <div className="p-6 bg-amber-50 border border-amber-200 rounded-xl text-center">
          <p className="text-sm font-semibold text-amber-800 mb-1">Sync returned no actuals data</p>
          <p className="text-xs text-amber-600">
            QuickBooks returned no time entries for this engagement. Try a different reference or add services manually.
          </p>
        </div>
      )}

      {!componentsLoading && !autoSyncing && !autoSyncError && services.length === 0 && selectedTemplate && !selectedTemplate.qb_customer_ref && (
        <div className="p-6 bg-amber-50 border border-amber-200 rounded-xl text-center">
          <p className="text-sm font-semibold text-amber-800 mb-1">No QuickBooks link</p>
          <p className="text-xs text-amber-600">
            This engagement has no QuickBooks customer reference. Edit the engagement to add one, or choose a different reference.
          </p>
        </div>
      )}

      {!componentsLoading && !autoSyncing && services.length === 0 && !selectedTemplate && !selectedBaseline && (
        <div className="p-6 bg-slate-50 border border-slate-200 rounded-xl text-center">
          <p className="text-sm text-slate-500">No services added yet.</p>
        </div>
      )}
      {selectedBaseline && (
        <div className="flex items-center gap-2 text-xs">
          <span className="inline-flex items-center px-2 py-0.5 rounded-full font-medium bg-violet-100 text-violet-700 border border-violet-200">Using {selectedBaseline.label} Baseline</span>
          <span className="text-grey-500">{selectedBaseline.annualHrs?.toFixed(1)} hrs/yr · ${selectedBaseline.monthlyFee?.toLocaleString()}/mo est.</span>
        </div>
      )}

      {/* Missing rate warning */}
      {!componentsLoading && !autoSyncing && services.length > 0 && missingRateRoles.length > 0 && (
        <div className="flex gap-3 p-4 bg-amber-50 border border-amber-200 rounded-xl">
          <ExclamationTriangleIcon className="w-5 h-5 text-amber-500 flex-shrink-0 mt-0.5" />
          <div className="space-y-1.5">
            <p className="text-sm font-semibold text-amber-800">Missing cost rates — margin may be inaccurate</p>
            <ul className="space-y-0.5">
              {missingRateRoles.map(({ role, totalHours }) => (
                <li key={role} className="text-xs text-amber-700">
                  {getRoleDisplayName(role)} — {totalHours.toFixed(1)} hrs at $0/hr
                </li>
              ))}
            </ul>
            <p className="text-xs text-amber-600">Set rates in the Bill Rates panel or change the role assignment.</p>
          </div>
        </div>
      )}

      {/* Main grid */}
      <div className="grid grid-cols-1 lg:grid-cols-3 gap-5">

        {/* Left column */}
        <div className="lg:col-span-2 space-y-3 order-2 lg:order-1">

          {/* Scenario details panel */}
          <ScenarioDetailsPanel
            scenarioName={scenarioName}
            setScenarioName={setScenarioName}
            clientName={clientName}
            setClientName={setClientName}
            notes={notes}
            setNotes={setNotes}
            isRetainer={isRetainer}
            engagementType={wizardData.engagementType}
            onSave={handleSaveScenario}
            saveStatus={saveStatus}
            savedScenarioId={savedScenarioId}
          />

          {/* Service cards */}
          <div data-tour="step4-services" className="space-y-3">
            {services.map((service, serviceIdx) => (
              <ServiceCard
                key={serviceIdx}
                service={service}
                serviceIdx={serviceIdx}
                roles={roles}
                effectiveRateMap={effectiveRateMap}
                ratesLoading={ratesLoading}
                onUpdateCategory={updateCategory}
                onUpdateRole={updateItemRole}
                onUpdateHours={updateItemHours}
                onAddItem={addLineItem}
                onRemoveItem={removeLineItem}
                onRemoveService={removeService}
                defaultExpanded={!isLoaded}
                missingRateSet={missingRateSet}
              />
            ))}
          </div>

          {/* Add service */}
          <button
            type="button"
            onClick={addService}
            className="w-full py-2.5 border-2 border-dashed border-grey-300 rounded-xl text-xs font-semibold text-grey-400 hover:border-darkblue-300 hover:text-darkblue-500 transition-colors flex items-center justify-center gap-1.5"
          >
            <PlusIcon className="w-4 h-4" />Add Service Category
          </button>
        </div>

        {/* Right sidebar — renders first on mobile, second on desktop */}
        <div className="space-y-4 order-1 lg:order-2">
          <div data-tour="step4-summary">
            <BidSummary calculations={calculations} isRetainer={isRetainer} hasMissingRates={missingRateRoles.length > 0} />
          </div>
          <div data-tour="step4-margin" className="bg-white border border-grey-200 rounded-xl p-4">
            <MarginControl margin={margin} onChange={setMargin} />
          </div>
          <MarketContext pricingContext={pricingContext} />
          <RatesSidebar
            roles={roles}
            rateMap={rateMap}
            ratesLoading={ratesLoading}
            overrides={rateOverrides}
            onOverrideChange={handleRateOverride}
            missingRateRoles={missingRateRoles}
          />
        </div>
      </div>
    </div>
  );
};

export default Step4BuildServices;